import { tobuKenzaiHoukokusyoDocumentType } from './classifications/tobuKenzaiHoukokusyo'
import { todaEnkabutsuDocumentType } from './classifications/todaEnkabutsu'
import { todaFreshDocumentType } from './classifications/todaFresh'
import { todaHoukokusyoDocumentType, todaHoukokusyoDocumentType2 } from './classifications/todaHoukokuyso'
import { todaSeisekisyoDocumentType } from './classifications/todaSeisekisyo'

// 印刷・出力（PDF結合）時の帳票種別のデフォルト並び順。
// 確定仕様（生コン #09 分割チケット）:
//   1. 打設計画書 → 2. 打設報告書 → 3. フレッシュコンクリート試験結果
//   → 4. 塩化物量測定結果 → 5. 圧縮強度試験成績書 → 6. その他帳票
// この配列に含まれない帳票種別（未知・OCR 未完了・「その他」・「納入書」）は末尾に回す。
// 納入書は帳票種別として識別・保存はするが、出力上は「その他」扱いのため意図的に載せていない
// （documentType.ts の DOCUMENT_TYPES のコメント参照）。
export const DOCUMENT_TYPE_PRINT_ORDER: readonly string[] = [
  todaHoukokusyoDocumentType2, // 打設計画書
  todaHoukokusyoDocumentType, // 打設報告書
  todaFreshDocumentType, // フレッシュコンクリート試験結果
  todaEnkabutsuDocumentType, // 塩化物量測定結果
  todaSeisekisyoDocumentType, // 圧縮強度試験成績書
]

// 圧縮強度試験報告書は成績書へ統合予定（#05）で本来は不要だが、統合前のデータが残っている間は
// 成績書と同じ位置（末尾より前）に並ぶよう、成績書と同順位で扱う。
const PRINT_RANK_BY_TYPE: ReadonlyMap<string, number> = new Map<string, number>([
  ...DOCUMENT_TYPE_PRINT_ORDER.map((type, index): [string, number] => [type, index]),
  [tobuKenzaiHoukokusyoDocumentType, DOCUMENT_TYPE_PRINT_ORDER.indexOf(todaSeisekisyoDocumentType)],
])

// 並び順が定義されていない帳票種別（その他帳票）を末尾へ回すための番兵値。
const OTHERS_PRINT_RANK = Number.MAX_SAFE_INTEGER

/**
 * 帳票種別の印刷順位を返す。未定義・null/undefined の種別は末尾（その他帳票）。
 * 数値が小さいほど先頭に並ぶ。
 */
export function documentTypePrintRank(documentType: string | null | undefined): number {
  if (documentType === null || documentType === undefined) {
    return OTHERS_PRINT_RANK
  }
  return PRINT_RANK_BY_TYPE.get(documentType) ?? OTHERS_PRINT_RANK
}

// 印刷順の比較に必要な最小限のフィールドのみを要求する（フロントの Fragment 型に依存しない）。
export type PrintableDocumentOrderFields = {
  documentType?: string | null
  pageNo: number
  reviewedAt?: string | null
}

/**
 * 印刷・出力時の帳票の並び順コンパレータ。ロット（TestResult）内の帳票配列を sort する用途。
 * 優先順位:
 *   1. 帳票種別順（DOCUMENT_TYPE_PRINT_ORDER。未定義は末尾）
 *   2. 同一種別内は元 PDF のページ番号（pageNo）昇順
 *   3. pageNo で決まらない場合は「データ確定」時刻（reviewedAt）の古い順
 * reviewedAt が無い（未確定）ものは、確定済みのものより後ろに回す。
 */
export function compareDocumentsForPrint(
  a: PrintableDocumentOrderFields,
  b: PrintableDocumentOrderFields,
): number {
  const rankDiff = documentTypePrintRank(a.documentType) - documentTypePrintRank(b.documentType)
  if (rankDiff !== 0) {
    return rankDiff
  }

  const pageDiff = a.pageNo - b.pageNo
  if (pageDiff !== 0) {
    return pageDiff
  }

  // reviewedAt は ISO8601 文字列なので辞書順比較で時系列順になる。
  // 未確定（null/undefined）は末尾へ。両方未確定なら同順とみなす。
  if (a.reviewedAt === b.reviewedAt) {
    return 0
  }
  if (a.reviewedAt === null || a.reviewedAt === undefined) {
    return 1
  }
  if (b.reviewedAt === null || b.reviewedAt === undefined) {
    return -1
  }
  return a.reviewedAt < b.reviewedAt ? -1 : 1
}
